import { Fragment, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, LogOut } from 'lucide-react'
import type { TFunction } from 'i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useProducts } from '@/hooks/useDirectory'
import { topBarTrail } from '@/lib/nav'
import type { AuthSession } from '@/lib/authToken'
import { useAuth } from '@/providers/AuthProvider'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}

/** "Tier 2 · Validated env." is the agent's `title` from /auth/me; without one, built from tier and team. */
function userSubtitle(user: AuthSession, t: TFunction): string {
  if (user.title) return user.title
  if (user.kind === 'CUSTOMER') return t('user.customer')
  if (user.tier) {
    const tier = t('user.tier', { tier: user.tier })
    return user.teamName && user.teamName !== tier ? `${tier} · ${user.teamName}` : tier
  }
  if (user.teamName) return user.teamName
  return user.isSuperAdmin ? t('user.superAdmin') : t('user.agent')
}

/** The bar over every page, as in cgt-frontend: where you are on the left, who you are on the right. */
export function TopBar() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { pathname, search } = useLocation()
  const { data: products } = useProducts()
  const trail = topBarTrail(pathname, search, user?.kind === 'CUSTOMER', products)

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-background px-6">
      <nav aria-label={t('nav.breadcrumb')} className="min-w-0 grow">
        <ol className="flex min-w-0 items-center gap-1.5 text-body">
          {trail.map((c, i) => {
            const label = c.labelKey ? t(c.labelKey) : c.text
            const last = i === trail.length - 1
            return (
              <Fragment key={`${i}:${label}`}>
                {i > 0 ? <ChevronRight className="size-3.5 shrink-0 text-ink-placeholder" aria-hidden /> : null}
                <li className="min-w-0 truncate">
                  {c.to ? (
                    <Link to={c.to} className="text-ink-muted hover:text-ink hover:underline">
                      {label}
                    </Link>
                  ) : (
                    <span className={last ? 'font-semibold text-ink' : 'text-ink-muted'} aria-current={last ? 'page' : undefined}>
                      {label}
                    </span>
                  )}
                </li>
              </Fragment>
            )
          })}
        </ol>
      </nav>
      {user ? <AccountMenu user={user} subtitle={userSubtitle(user, t)} /> : null}
    </header>
  )
}

function AccountMenu({ user, subtitle }: { user: AuthSession; subtitle: string }) {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('nav.account', { name: user.name })}
          className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sidebar-avatar text-label font-semibold text-primary-foreground">
            {initials(user.name)}
          </span>
          <span className="hidden min-w-0 flex-col text-left leading-tight sm:flex">
            <span className="max-w-[180px] truncate text-meta font-medium text-ink">{user.name}</span>
            <span className="max-w-[180px] truncate text-label text-ink-muted">{subtitle}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-1">
        <div className="border-b border-line px-2.5 py-2">
          <p className="truncate text-meta font-medium text-ink">{user.name}</p>
          <p className="truncate text-label text-ink-muted">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="mt-1 flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-meta text-ink-body hover:bg-surface-subtle"
        >
          <LogOut className="size-4" aria-hidden /> {t('nav.signOut')}
        </button>
      </PopoverContent>
    </Popover>
  )
}
